import React, { useState, useMemo } from 'react';
import SEO from '../../Components/SEO';
import Breadcrumb from '../../Components/Breadcrumb';
import speakingTopics from '../Speaking-Exercises/Data/speakingtopics.json';
import './vocabulary-list.css';

const vocabListImg = '/Images/vocabulary-list-hero.webp';

const stopWords = new Set([
  'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
  'may', 'might', 'must', 'can', 'to', 'of', 'in', 'for', 'on', 'with', 'at',
  'by', 'from', 'as', 'into', 'through', 'during', 'before', 'after', 'above',
  'below', 'between', 'under', 'again', 'further', 'then', 'once', 'here',
  'there', 'when', 'where', 'why', 'how', 'all', 'each', 'few', 'more',
  'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own',
  'same', 'so', 'than', 'too', 'very', 'just', 'also', 'now', 'you', 'your',
  'i', 'me', 'my', 'we', 'our', 'they', 'them', 'their', 'he', 'she',
  'it', 'its', 'what', 'which', 'who', 'whom', 'this', 'that', 'these', 'those',
  'am', 'if', 'or', 'and', 'but', 'because', 'about', 'tell', 'think', 'know',
  'like', 'get', 'go', 'come', 'make', 'say', 'take', 'see', 'want', 'use',
  'time', 'way', 'thing', 'people', 'year', 'good', 'much', 'most', 'very',
  'really', 'ever', 'never', 'always', 'sometimes', 'often', 'usually', 'actually',
  'probably', 'maybe', 'certainly', 'definitely', 'especially', 'particularly',
  'generally', 'normally', 'basically', 'simply', 'completely', 'absolutely', 'exactly',
  'already', 'enough', 'finally', 'recently', 'last', 'next', 'first', 'new', 'old',
  'great', 'big', 'small', 'long', 'short', 'high', 'low', 'right', 'left',
  'whole', 'part', 'sort', 'type', 'able', 'sure', 'feel', 'let', 'try', 'put',
  'give', 'ask', 'work', 'seem', 'need', 'keep', 'start', 'begin', 'grow', 'live',
  'play', 'run', 'move', 'study', 'show', 'buy', 'wait', 'serve', 'die', 'send',
  'expect', 'build', 'stay', 'fall', 'cut', 'reach', 'kill', 'remain', 'consider',
  'appear', 'hope', 'thank', 'father', 'mother', 'child', 'man', 'woman', 'girl',
  'boy', 'friend', 'family', 'teacher', 'student', 'job', 'day', 'week', 'month',
  'house', 'place', 'country', 'city', 'world', 'life', 'hand', 'side', 'face',
  'fact', 'matter', 'night', 'point', 'home', 'water', 'room', 'idea', 'light',
  'word', 'money', 'book', 'eye', 'office', 'door', 'number', 'name', 'school',
  'question', 'problem', 'story', 'program', 'company', 'system', 'someone', 'anyone',
  'everyone', 'something', 'anything', 'everything', 'nothing', 'one', 'two', 'three'
]);

const extractVocabulary = (texts) => {
  const wordCounts = {};
  
  texts.forEach(text => {
    const words = text.toLowerCase()
      .replace(/[^\w\s']/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.has(w) && !/^\d+$/.test(w));
    
    words.forEach(word => {
      const cleanWord = word.replace(/^'|'$/g, '');
      if (cleanWord.length > 2) {
        wordCounts[cleanWord] = (wordCounts[cleanWord] || 0) + 1;
      }
    });
  });
  
  return Object.entries(wordCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 20)
    .map(([word, count]) => ({ word, count }));
};

const basicDefinitions = {
  'election': { en: 'election', hu: 'választás' },
  'responsibility': { en: 'responsibility', hu: 'felelősség' },
  'parliamentary': { en: 'parliamentary', hu: 'parlamenti' },
  'minister': { en: 'minister', hu: 'miniszter' },
  'democracy': { en: 'democracy', hu: 'demokrácia' },
  'political': { en: 'political', hu: 'politikai' },
  'domestically': { en: 'domestically', hu: 'belföldön' },
  'discourse': { en: 'discourse', hu: 'párbeszéd' },
  'outcomes': { en: 'outcomes', hu: 'eredmények' },
  'pressing': { en: 'pressing', hu: 'sürgős' },
  'benefits': { en: 'benefits', hu: 'előnyök' },
  'drawbacks': { en: 'drawbacks', hu: 'hátrányok' },
  'represent': { en: 'represent', hu: 'képvisel' },
  'global': { en: 'global', hu: 'globális' },
  'historical': { en: 'historical', hu: 'történelmi' },
  'contemporary': { en: 'contemporary', hu: 'kortárs' },
  'citizens': { en: 'citizens', hu: 'állampolgárok' },
  'values': { en: 'values', hu: 'értékek' },
  'platform': { en: 'platform', hu: 'politikai program' },
  'protest': { en: 'protest', hu: 'tüntetés' },
  'democratic': { en: 'democratic', hu: 'demokratikus' },
  'government': { en: 'government', hu: 'kormány' },
  'citizen': { en: 'citizen', hu: 'állampolgár' },
  'voting': { en: 'voting', hu: 'szavazás' },
  'leader': { en: 'leader', hu: 'vezető' },
  'society': { en: 'society', hu: 'társadalom' },
  'rights': { en: 'rights', hu: 'jogok' },
  'freedom': { en: 'freedom', hu: 'szabadság' },
  'law': { en: 'law', hu: 'törvény' },
  'power': { en: 'power', hu: 'hatalom' },
  'justice': { en: 'justice', hu: 'igazságszolgáltatás' },
  'current': { en: 'current', hu: 'aktuális' },
  'events': { en: 'events', hu: 'események' },
  'influence': { en: 'influence', hu: 'befolyásol' },
  'international': { en: 'international', hu: 'nemzetközi' },
  'policy': { en: 'policy', hu: 'politika' },
  'parties': { en: 'parties', hu: 'pártok' },
  'media': { en: 'media', hu: 'média' },
  'issues': { en: 'issues', hu: 'kérdések' },
  'process': { en: 'process', hu: 'folyamat' },
  'opinion': { en: 'opinion', hu: 'vélemény' },
  'discussion': { en: 'discussion', hu: 'vita' },
  'economic': { en: 'economic', hu: 'gazdasági' },
  'generational': { en: 'generational', hu: 'generációs' },
  'treaties': { en: 'treaties', hu: 'szerződések' },
  'sovereignty': { en: 'sovereignty', hu: 'szuverenitás' },
  'activism': { en: 'activism', hu: 'aktivizmus' },
  'concept': { en: 'concept', hu: 'fogalom' },
  'manager': { en: 'manager', hu: 'vezető' },
  'technical': { en: 'technical', hu: 'technikai' },
  'pressure': { en: 'pressure', hu: 'nyomás' },
  'presentation': { en: 'presentation', hu: 'előadás' },
  'feedback': { en: 'feedback', hu: 'visszajelzés' },
  'dream': { en: 'dream', hu: 'álom' },
  'goal': { en: 'goal', hu: 'cél' },
  'believe': { en: 'believe', hu: 'hinni' },
  'visited': { en: 'visited', hu: 'meglátogatott' },
  'accident': { en: 'accident', hu: 'baleset' },
  'happy': { en: 'happy', hu: 'boldog' },
  'sad': { en: 'sad', hu: 'szomorú' },
  'excited': { en: 'excited', hu: 'izgatott' },
  'frightened': { en: 'frightened', hu: 'megijedt' },
  'beautiful': { en: 'beautiful', hu: 'szép' },
  'ugly': { en: 'ugly', hu: 'csúnya' },
  'delicious': { en: 'delicious', hu: 'finom' },
  'pleasant': { en: 'pleasant', hu: 'kellemes' },
  'boring': { en: 'boring', hu: 'unalmas' },
  'teacher': { en: 'teacher', hu: 'tanár' },
  'regret': { en: 'regret', hu: 'megbánás' },
  'mistake': { en: 'mistake', hu: 'hiba' },
  'book': { en: 'book', hu: 'könyv' },
  'achieved': { en: 'achieved', hu: 'elért' },
  'punishment': { en: 'punishment', hu: 'büntetés' },
  'movie': { en: 'movie', hu: 'film' },
  'music': { en: 'music', hu: 'zene' },
  'vacation': { en: 'vacation', hu: 'szabadság' },
  'scares': { en: 'scares', hu: 'félelmetes' },
  'productive': { en: 'productive', hu: 'termelő' },
  'traveled': { en: 'traveled', hu: 'utazott' },
  'lost': { en: 'lost', hu: 'elveszett' },
  'danced': { en: 'danced', hu: 'táncolt' },
  'drank': { en: 'drank', hu: 'ivott' },
  'swimming': { en: 'swimming', hu: 'úszás' },
  'hometown': { en: 'hometown', hu: 'szülőváros' },
  'restaurant': { en: 'restaurant', hu: 'étterem' },
  'meetings': { en: 'meetings', hu: 'megbeszélések' },
  'deadlines': { en: 'deadlines', hu: 'határidők' },
  'troubleshoot': { en: 'troubleshoot', hu: 'hibaelhárítás' },
  'team': { en: 'team', hu: 'csapat' },
  'delivery': { en: 'delivery', hu: 'szállítás' },
  'vegetables': { en: 'vegetables', hu: 'zöldségek' },
  'weights': { en: 'weights', hu: 'súlyok' },
  'bicycle': { en: 'bicycle', hu: 'kerékpár' },
  'candies': { en: 'candies', hu: 'cukorkák' },
  'mall': { en: 'mall', hu: 'bevásárlóközpont' },
  'fast': { en: 'fast', hu: 'gyors' },
  'broken': { en: 'broken', hu: 'törött' },
  'appearance': { en: 'appearance', hu: 'megjelenés' },
  'cheated': { en: 'cheated', hu: 'csalt' },
  'exam': { en: 'exam', hu: 'vizsga' },
  'surfing': { en: 'surfing', hu: 'szörfözés' },
  'motorcycle': { en: 'motorcycle', hu: 'motorkerékpár' },
  'cave': { en: 'cave', hu: 'barlang' },
  'tent': { en: 'tent', hu: 'sátor' },
  'ocean': { en: 'ocean', hu: 'óceán' },
  'blackout': { en: 'blackout', hu: 'áramszünet' },
  'hitchhiked': { en: 'hitchhiked', hu: 'stoppal' },
  'fainted': { en: 'fainted', hu: 'elájult' },
  'project': { en: 'project', hu: 'projekt' },
  'conflict': { en: 'conflict', hu: 'konfliktus' },
  'career': { en: 'career', hu: 'karrier' },
  'technology': { en: 'technology', hu: 'technológia' },
  'tourism': { en: 'tourism', hu: 'turizmus' },
  'customs': { en: 'customs', hu: 'szokások' },
  'traditions': { en: 'traditions', hu: 'hagyományok' },
  'greet': { en: 'greet', hu: 'köszön' },
  'wedding': { en: 'wedding', hu: 'esküvő' },
  'gadgets': { en: 'gadgets', hu: 'készülékek' },
  'fascinated': { en: 'fascinated', hu: 'lenyűgözött' },
  'advances': { en: 'advances', hu: 'fejlesztések' },
  'distracted': { en: 'distracted', hu: 'elterelt' },
  'addicted': { en: 'addicted', hu: 'függő' },
  'tablet': { en: 'tablet', hu: 'táblagép' },
  'screen': { en: 'screen', hu: 'képernyő' },
  'fomo': { en: 'FOMO', hu: ' Fear of Missing Out' },
  'virar': { en: 'viral', hu: 'vírusos' },
  'spoofs': { en: 'spoofs', hu: 'hamisítások' },
  'anonymity': { en: 'anonymity', hu: 'anonymitás' },
  'privacy': { en: 'privacy', hu: 'adatosvédelem' },
  'scammed': { en: 'scammed', hu: ' átvert' },
  'passwords': { en: 'passwords', hu: 'jelszavak' },
  'support': { en: 'support', hu: 'támogatás' },
  'social': { en: 'social', hu: 'társasági' },
  'balance': { en: 'balance', hu: 'egyensúly' },
  'siblings': { en: 'siblings', hu: 'testvérek' },
  'tradition': { en: 'tradition', hu: 'hagyomány' },
  'hobbies': { en: 'hobbies', hu: 'hobbik' },
  'uniforms': { en: 'uniforms', hu: 'egyenruhák' },
  'extracurricular': { en: 'extracurricular', hu: 'szakkörök' },
  'homework': { en: 'homework', hu: 'házi feladat' },
  'attendance': { en: 'attendance', hu: 'jelenlét' },
  'uniform': { en: 'uniform', hu: 'egyenruha' },
  'clothing': { en: 'clothing', hu: 'ruházat' },
  'expensive': { en: 'expensive', hu: 'drága' },
  'designer': { en: 'designer', hu: 'designer' },
  'bullying': { en: 'bullying', hu: 'bullying' },
  'hazing': { en: 'hazing', hu: 'beavatás' },
  'prom': { en: 'prom', hu: 'gálavacsora' },
  'standardized': { en: 'standardized', hu: 'szabványosított' },
  'tutor': { en: 'tutor', hu: 'tanácsadó' },
  'immersion': { en: 'immersion', hu: 'merítés' },
  'cognitive': { en: 'cognitive', hu: 'kognitív' },
  'grammar': { en: 'grammar', hu: 'nyelvtan' },
  'vocabulary': { en: 'vocabulary', hu: 'szókincs' },
  'pronunciation': { en: 'pronunciation', hu: 'kiejtés' },
  'fluent': { en: 'fluent', hu: 'folyékony' },
  'slang': { en: 'slang', hu: 'szleng' },
  'colloquial': { en: 'colloquial', hu: 'köznyelvi' },
  'bilingual': { en: 'bilingual', hu: 'kétnyelvű' },
  'culture': { en: 'culture', hu: 'kultúra' },
  'heritage': { en: 'heritage', hu: 'örökség' },
  'globalization': { en: 'globalization', hu: 'globalizáció' },
  'festivals': { en: 'festivals', hu: 'fesztiválok' },
  'celebration': { en: 'celebration', hu: 'ünneplés' },
  'cuisine': { en: 'cuisine', hu: 'konyha' },
  'art': { en: 'art', hu: 'művészet' },
  'literature': { en: 'literature', hu: 'irodalom' },
  'religion': { en: 'religion', hu: 'vallás' },
  'spirituality': { en: 'spirituality', hu: 'spiritualitás' },
  'gender': { en: 'gender', hu: 'nem' },
  'storytelling': { en: 'storytelling', hu: 'történetmesélés' },
  'migration': { en: 'migration', hu: 'vándorlás' },
  'pollution': { en: 'pollution', hu: 'szennyezés' },
  'recycle': { en: 'recycle', hu: 'újrahasznosít' },
  'alternative': { en: 'alternative', hu: 'alternatíva' },
  'renewable': { en: 'renewable', hu: 'megújuló' },
  'sustainable': { en: 'sustainable', hu: 'fenntartható' },
  'climate': { en: 'climate', hu: 'éghajlat' },
  'warming': { en: 'warming', hu: 'felmelegedés' },
  'energy': { en: 'energy', hu: 'energia' },
  'conservation': { en: 'conservation', hu: 'megőrzés' },
  'endangered': { en: 'endangered', hu: 'veszélyeztetett' },
  'habitat': { en: 'habitat', hu: 'élőhely' },
  'ecosystem': { en: 'ecosystem', hu: 'ökoszisztéma' },
  'species': { en: 'species', hu: 'fajok' },
  'spending': { en: 'spending', hu: 'költés' },
  'saver': { en: 'saver', hu: 'takarékos' },
  'wealth': { en: 'wealth', hu: 'vagyon' },
  'investing': { en: 'investing', hu: 'befektetés' },
  'stock': { en: 'stock', hu: 'részvény' },
  'lottery': { en: 'lottery', hu: 'lottó' },
  'charity': { en: 'charity', hu: 'jótékonyság' },
  'tipping': { en: 'tipping', hu: 'borravaló' },
  'homeless': { en: 'homeless', hu: 'hajléktalan' },
  'gamble': { en: 'gamble', hu: 'szerencsejáték' },
  'competitive': { en: 'competitive', hu: 'versengő' },
  'defeat': { en: 'defeat', hu: 'vereség' },
  'industry': { en: 'industry', hu: 'ipar' },
  'strategy': { en: 'strategy', hu: 'stratégia' },
  'victory': { en: 'victory', hu: 'győzelem' },
  'teamwork': { en: 'teamwork', hu: 'csapatmunka' },
  'pets': { en: 'pets', hu: 'háziállatok' },
  'adopt': { en: 'adopt', hu: 'örökbe fogad' },
  'shelter': { en: 'shelter', hu: 'menhely' },
  'trained': { en: 'trained', hu: 'kiképzett' },
  'graduate': { en: 'graduate', hu: 'diplomás' },
  'tourist': { en: 'tourist', hu: 'turista' },
  'airport': { en: 'airport', hu: 'repülőtér' },
  'passport': { en: 'passport', hu: 'útlevél' },
  'luggage': { en: 'luggage', hu: 'poggyász' },
  'security': { en: 'security', hu: 'biztonság' },
  'boarding': { en: 'boarding', hu: 'beszállás' }
};

const VocabularyList = () => {
  const [selectedCategory, setSelectedCategory] = useState(speakingTopics[0]?.title || '');

  const categories = speakingTopics.map(topic => topic.title);

  const vocabularyByCategory = useMemo(() => {
    const vocab = {};
    speakingTopics.forEach(topic => {
      const texts = topic.content.filter(t => t.trim() && !t.startsWith('---'));
      vocab[topic.title] = extractVocabulary(texts);
    });
    return vocab;
  }, []);

  const categoryColors = {
    'Current Events & Political Engagement': '#e74c3c',
    'Can you tell me about… Questions': '#3498db',
    'When was the last time… Questions': '#9b59b6',
    'How often… Questions': '#1abc9c',
    'Have you ever… Questions': '#f39c12',
    'How long… Questions': '#e67e22',
    'If… Questions': '#34495e',
    'Would you ever… Questions': '#16a085',
    'Conditional Conversation Questions': '#8e44ad',
    'Careers and work': '#2980b9',
    'Holidays & travel': '#27ae60',
    'The Internet and new technologies': '#c0392b',
    'Friends, family and social life': '#d35400',
    'School, college and university': '#7f8c8d',
    'Language learning': '#2c3e50',
    'Culture and traditions': '#9b59b6',
    'Environment': '#27ae60',
    'Current Events': '#e74c3c',
    'Money and Consumerism': '#f1c40f',
    'Competition': '#e84393',
    'Animals and pets': '#00b894',
    'Miscellaneous Questions': '#636e72'
  };

  return (
    <div>
      <SEO
        title="Free English Vocabulary Lists"
        description="Filterable vocabulary word list with definitions from speaking topics. Browse by category: Current Events, Careers, Travel, Technology, and more."
        name="English Exam Exercises"
        type="article"
        image={vocabListImg}
      />
      <Breadcrumb />

      <div className="vocab-list-container">
        <div className="vocab-list-header">
          <h1>Vocabulary List</h1>
        </div>

        <div className="vocab-category-filters">
          {categories.map(cat => (
            <button
              key={cat}
              className={`vocab-category-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
              style={selectedCategory === cat ? { backgroundColor: categoryColors[cat] || '#3498db' } : {}}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="vocab-current-category">
          <h2 style={{ color: categoryColors[selectedCategory] || '#3498db' }}>
            {selectedCategory}
          </h2>
        </div>

        <div className="vocab-word-list">
          {(vocabularyByCategory[selectedCategory] || [])
            .filter(item => basicDefinitions[item.word])
            .map((item, index) => {
              return (
                <div key={index} className="vocab-entry">
                  <span className="vocab-word">{item.word}</span>
                  <span className="vocab-def"> - {basicDefinitions[item.word].hu}</span>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default VocabularyList;
